import { Alert, Button, Checkbox } from '@design-system';
import { AddressElement } from '@stripe/react-stripe-js';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';

import { csrClient } from '../../../../apollo/api/avantArteClient';
import { COUNTRY_CODES } from '../../../../constants/locations';
import AvantArteAccountGateway from '../../../../gateways/AvantArteAccountGateway';
import { useFlow } from '../../../../providers';
import { usePreAuthDrawCtx } from '../../../../providers/PreAuthDrawProvider';
import { useAnalyticsEmitter } from '../../../../utils/analytics/emitter';
import { useCustomsCode } from '../../hooks/useCustomsCode';
import { AddCustomsCode } from './AddCustomsCode';

const allowedCountries = COUNTRY_CODES.map((c) => c);
const accountGateway = new AvantArteAccountGateway(csrClient);

interface StripeAddress {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    country: string;
  };
  phone?: string | null;
}

export const AddShippingAddress: React.FC = () => {
  const { refreshAddresses } = usePreAuthDrawCtx();
  const { t } = useTranslation(['product', 'countries']);
  const { currentStepId, goToStep } = useFlow();
  const [stripeDetails, setStripeDetails] = useState<StripeAddress>({});
  const [complete, setComplete] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [working, setWorking] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [emitter] = useAnalyticsEmitter();
  const customsCode = useCustomsCode(stripeDetails?.address?.country);

  async function handleAddressCreation() {
    if (working || !complete) return;

    setWorking(true);

    try {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const address = stripeDetails.address!;
      const [firstName, lastName] = (stripeDetails.name ?? '').split(
        /[ ]{1}(.*)/,
      );

      const response = await accountGateway.createAddress({
        isDefault,
        addressLine1: address.line1 || void 0,
        addressLine2: address.line2 || void 0,
        city: address.city || void 0,
        countryName: `${t(`countries:${address.country}`)}`,
        countryCode: address.country,
        customsCode: customsCode.value,
        firstName,
        lastName,
        phone: stripeDetails.phone ?? void 0,
        provinceName: address.state ?? void 0,
        zip: address.postal_code ?? void 0,
      });

      if ('error' in response) {
        switch (response.error.code) {
          default:
            setErrorMessage(
              t(
                'product:drawers.review.draw.address.addAddress.failureMessageGeneric',
              ),
            );
        }

        return;
      }

      setErrorMessage(void 0);
      emitter('new details', 'saved');
      refreshAddresses(response.id);
      goToStep('apply');
    } catch (err) {
      console.error(err);
      customsCode.setError(customsCode.required);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="flex flex-col">
      {errorMessage && (
        <div className="mt-8">
          <Alert status="error">{errorMessage}</Alert>
        </div>
      )}
      {currentStepId === 'add_shipping_address' && (
        <div>
          <AddressElement
            onChange={(event) => {
              setComplete(event.complete);
              setStripeDetails(event.value);
            }}
            options={{
              allowedCountries,
              mode: 'shipping',
              autocomplete: {
                mode: 'automatic',
              },
              display: {
                name: 'full',
              },
              fields: {
                phone: 'always',
              },
              validation: {
                phone: {
                  required: 'always',
                },
              },
            }}
          />
        </div>
      )}
      {stripeDetails?.address?.country && customsCode.required && (
        <AddCustomsCode
          className="mt-3"
          countryCode={stripeDetails.address.country}
          error={customsCode.error}
          onChange={(value) => {
            customsCode.setError(false);
            customsCode.setValue(value);
          }}
        />
      )}
      <div className="my-8">
        <Checkbox
          checked={isDefault}
          disabled={working}
          id="isDefaultAddress"
          label={t(
            'product:drawers.review.draw.address.addAddress.defaultButton',
          )}
          name="isDefaultAddress"
          onChange={(e) => setIsDefault(e.currentTarget.checked)}
          type="checkbox"
          value="isDefault"
        />
      </div>
      <Button
        disabled={working || !complete || !customsCode.valid}
        fullWidth
        label={t('drawers.review.draw.address.addAddress.saveButton')}
        onClick={() => {
          handleAddressCreation();
        }}
        variant="primary"
      />
    </div>
  );
};
